import { GridOptions } from '@ag-grid-community/core'
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { AccountDetailsKey } from '@helios/panels/account-details'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import {
  ModalLinkRendererParams,
  NgGridComponents,
} from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  ClientCustodyContact,
  TransportDetails,
} from '../../models/account-details.model'
import { AccountDetailsService } from '../../services/account-details.service'
import { CustodyContactsModalComponent } from './custody-contacts-modal/custody-contacts-modal.component'

@Component({
  selector: 'hs-transport-details',
  templateUrl: './transport-details-grid.component.html',
  styleUrls: ['./transport-details-grid.component.scss'],
})
export class TransportDetailsGridComponent implements OnDestroy {
  /**
   * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
   * preferences for this grid.
   */
  TransportListMainGridKey = `${AccountDetailsKey}-transportsGrid`

  constructor(
    public accountDetailsService: AccountDetailsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    readonly modalService: NgbModal,
  ) {}
  rowTransportDetailsData: TransportDetails[] = []

  custodyContacts: ClientCustodyContact[] = []
  loading = false

  private readonly transportDetailsField = composeFieldPath(
    this.rowTransportDetailsData,
  )

  private readonly destroySubject$ = new Subject<void>()

  gridOptionsTransportDetails: GridOptions = {
    sideBar: false,
    ...heliosSizingAdjustments,
    onGridReady: params => {
      params.api.setDomLayout('autoHeight')

      this.savedViewService.manageSavedView(
        params,
        this.TransportListMainGridKey,
        this.destroySubject$,
      )
      this.preferences
        .getDensityLevel()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(level =>
          enableHeliosGridDensityLevel(
            params.api,
            DensityLevel.Compact === level,
          ),
        )
      enableHeliosCopyBehavior(params.api)
    },
    columnDefs: [
      {
        field: this.transportDetailsField('name'),
        headerName: 'Name',
        width: 300,
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: this.transportDetailsField('dataFeedId'),
        headerName: 'Data Feed ID',
        width: heliosDefaultColWidth.Default,
        minWidth: heliosDefaultColWidth.Default,
      },
      {
        field: this.transportDetailsField('contactSize'),
        headerName: 'Contacts',
        width: heliosDefaultColWidth.Id,
        cellRenderer: undefined,
        cellRendererSelector: () => {
          return this.getModalOverRenderer()
        },
      },
    ],
  }

  generateTransportDetails(accountId?: number) {
    this.loading = true
    this.apiPromises(accountId).then(
      ([transportDetailsList, custodyContactDetails]) => {
        this.rowTransportDetailsData = transportDetailsList
        this.rowTransportDetailsData.forEach(function (detail) {
          detail.contacts = custodyContactDetails
          detail.contactSize = custodyContactDetails.length
        })
        this.loading = false
      },
    )
  }

  apiPromises(
    accountId?: number,
  ): Promise<[TransportDetails[], ClientCustodyContact[]]> {
    return Promise.all([
      this.accountDetailsService
        .getTransportDetails(accountId?.toString())
        .toPromise(),
      this.accountDetailsService
        .getCustodyContacts(accountId?.toString())
        .toPromise(),
    ])
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  /**
   *  loads the account selected
   *
   * @param accountId account id
   */
  @Input()
  set accountSelectionChanged(accountId: number) {
    this.generateTransportDetails(accountId)
  }
  getModalOverRenderer() {
    return {
      component: NgGridComponents.modalLinkRenderer,
      params: {
        modalComponent: CustodyContactsModalComponent,
        modalOptions: {
          size: 'lg',
          windowClass: 'modal-window-click-through',
        } as NgbModalOptions,
      } as ModalLinkRendererParams,
    }
  }
}
